import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import APICall, { GetUploadMediaBaseLocation } from "../../Utility/APICall"; // Combined import
import { paths, cloudStorageBucket } from "../../Utility/Constants";
import { useAuth } from "../../Utility/AuthProvider";
import { useUser } from "../../Utility/UserProvider";
import FileUpload from "../../Utility/FileUpload"; // Assume this component is stylable or already styled
import FileDownload from "../../Utility/FileDownload"; // Assume this component is stylable or already styled

// Using react-icons for consistency
import { FaPlusCircle, FaMinusCircle } from "react-icons/fa";

// Removed Layout import assuming it's handled by the router's <Outlet />

// Firebase imports (ensure firebase is initialized elsewhere)
import { storage } from "../../Utility/firebase";
import { getDownloadURL, ref, uploadBytesResumable } from "firebase/storage";

// --- Helper: Form Input Component ---
const FormInput = ({
  label,
  id,
  name,
  placeholder,
  value,
  onChange,
  type = "text",
  error,
}) => (
  <div className="mb-4">
    <label
      htmlFor={id}
      className="block text-sm font-medium text-gray-700 mb-1"
    >
      {label}
    </label>
    <input
      type={type}
      id={id}
      name={name}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      className={`block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm 
                 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm
                 ${error ? "border-red-500" : ""}`}
    />
    {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
  </div>
);

// --- Helper: Number Stepper Component ---
const NumberStepper = ({ label, value, onIncrement, onDecrement, min = 1 }) => (
  <div className="flex justify-between items-center py-3 border-b border-gray-200">
    <p className="text-sm text-gray-700">{label}</p>
    <div className="flex items-center gap-3">
      <button
        type="button"
        onClick={onDecrement}
        disabled={value <= min}
        className="p-1 rounded-full text-gray-500 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
        aria-label={`Decrement ${label}`}
      >
        <FaMinusCircle className="w-5 h-5" />
      </button>
      <p className="text-sm font-medium text-gray-800 w-6 text-center">
        {value}
      </p>
      <button
        type="button"
        onClick={onIncrement}
        className="p-1 rounded-full text-gray-500 hover:bg-gray-200"
        aria-label={`Increment ${label}`}
      >
        <FaPlusCircle className="w-5 h-5" />
      </button>
    </div>
  </div>
);

const AddNewBuilding = () => {
  const { jwt, user } = useAuth();
  // const { user } = useUser();
  const navigate = useNavigate();
  const location = useLocation();
  const buildingToEdit = location.state?.building || {}; // Renamed for clarity

  const amenitiesList = [
    "Free Parking",
    "Gate Security",
    "CCTV",
    "WiFi",
    "Power Backup",
    "Lift",
  ]; // Added more examples

  const [formData, setFormData] = useState({
    buildingName: buildingToEdit.building_name || "",
    locality: buildingToEdit.address || "",
    numberOfFlats:
      buildingToEdit.number_of_flats || buildingToEdit.no_of_units || 1, // Handle both possible field names
    numberOfFloors: buildingToEdit.number_of_floors || 1,
    amenities: buildingToEdit.amenities || [], // Ensure amenities is an array
    // imageUrl: buildingToEdit.imageUrl || "", // Handled by 'file' and 'existingImageUrl'
  });

  const [file, setFile] = useState(null); // For new file upload
  // State for the object URL created for the new file preview
  const [filePreview, setFilePreview] = useState(null);
  // const [existingImageUrl, setExistingImageUrl] = useState(
  //   buildingToEdit.media && buildingToEdit.media.length > 0
  //     ? buildingToEdit.media[0].location
  //     : ""
  // );
  // Your existing state for the image already on the server
  const [existingImageUrl, setExistingImageUrl] = useState(
    buildingToEdit.media &&
      buildingToEdit.media.length > 0 &&
      buildingToEdit.media[0].location
      ? buildingToEdit.media[0].location
      : buildingToEdit.media &&
          buildingToEdit.media.length > 0 &&
          buildingToEdit.media[0].url
        ? buildingToEdit.media[0].url
        : ""
  );

  // To manage deletion of old image from Firebase (similar to AddNewUnit)
  const [oldImageLocationToDelete, setOldImageLocationToDelete] =
    useState(null);

  const [uploadProgress, setUploadProgress] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(""); // For general form errors

  const isEditMode = !!buildingToEdit.entity_id; // Check if we are editing an existing building

  useEffect(() => {
    // If editing, and there's an image location, try to get a download URL for display
    // This logic depends on how you store/retrieve image URLs for existing buildings.
    // For Firebase Storage, you'd getDownloadURL for the existing location.
    // For now, we'll assume `existingImageUrl` is directly usable or handled by `FileDownload`.
    if (isEditMode && buildingToEdit.media && buildingToEdit.media.length > 0) {
      // If `FileDownload` needs a full URL, you might need to construct it here or fetch it.
      // For simplicity, if buildingToEdit.media[0].url is available, use it.
      // setExistingImageUrl(buildingToEdit.media[0].url || buildingToEdit.media[0].location);
    }
  }, [isEditMode, buildingToEdit]);

  useEffect(() => {
    // This function will be called when the component unmounts or when filePreview changes.
    return () => {
      if (filePreview) {
        URL.revokeObjectURL(filePreview);
      }
    };
  }, [filePreview]); // Dependency array: effect runs if filePreview changes

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
  };

  const handleCheckboxChange = (event) => {
    const { id, checked } = event.target;
    setFormData((prevData) => ({
      ...prevData,
      amenities: checked
        ? [...prevData.amenities, id]
        : prevData.amenities.filter((amenity) => amenity !== id),
    }));
  };

  // const handleFileUpload = (uploadedFile) => {
  //   setFile(uploadedFile);
  //   setExistingImageUrl("");
  // };

  // AddNewBuilding.jsx

  const handleFileUpload = (selectedFileList) => {
    // Renamed param to reflect it's a FileList
    // 1. Clean up any existing preview for a *newly selected* file
    if (filePreview) {
      URL.revokeObjectURL(filePreview);
      setFilePreview(null);
    }
    setFile(null); // Clear previous File object

    // 2. Process the new selection
    if (selectedFileList && selectedFileList.length > 0) {
      const newFileObject = selectedFileList[0]; // Get the first (and likely only) file
      setFile(newFileObject);
      setFilePreview(URL.createObjectURL(newFileObject));

      // 3. If replacing an existing image, mark the old one for deletion from Firebase
      //    and clear its display.
      if (
        existingImageUrl &&
        buildingToEdit.media &&
        buildingToEdit.media.length > 0 &&
        buildingToEdit.media[0].location
      ) {
        setOldImageLocationToDelete(buildingToEdit.media[0].location);
      }
      setExistingImageUrl(""); // Clear display of the server-persisted image
    } else {
      // No file selected (e.g., user cancelled file dialog)
      // file and filePreview are already null or set to null above.
      // If you want to restore the display of 'existingImageUrl' if a user cancels a new selection:
      const originalExistingImageUrl =
        buildingToEdit.media &&
        buildingToEdit.media.length > 0 &&
        buildingToEdit.media[0].location
          ? buildingToEdit.media[0].location
          : buildingToEdit.media &&
              buildingToEdit.media.length > 0 &&
              buildingToEdit.media[0].url
            ? buildingToEdit.media[0].url
            : "";
      if (!file) {
        // Only restore if no new file is set
        setExistingImageUrl(originalExistingImageUrl);
        setOldImageLocationToDelete(null); // Don't delete original if selection was cancelled
      }
    }
  };

  // --- API Call Logic (Simplified for this example, add more robust error handling) ---
  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsLoading(true);
    setError("");

    const buildingPayload = {
      building_name: formData.buildingName,
      number_of_flats: formData.numberOfFlats,
      number_of_floors: formData.numberOfFloors,
      address: formData.locality,
      amenities: formData.amenities, // Ensure this is an array of strings
      status: "active", // Or get from form
    };

    try {
      let buildingId = buildingToEdit.entity_id;
      let operationSuccessMessage = "";

      if (isEditMode) {
        // --- UPDATE EXISTING BUILDING ---
        buildingPayload.building_id = buildingId; // Add building_id for update
        const updateRes = await APICall(
          "PUT",
          paths.building.update,
          buildingPayload,
          undefined,
          jwt
        );
        if (updateRes.status !== "success")
          throw new Error(
            updateRes.message || "Failed to update building details."
          );
        operationSuccessMessage = "Building updated successfully!";
      } else {
        // --- ADD NEW BUILDING ---
        const addRes = await APICall(
          "POST",
          paths.building.add,
          buildingPayload,
          undefined,
          jwt
        );
        if (addRes.status !== "success" || !addRes.response?.entity_id) {
          throw new Error(
            addRes.message || "Failed to add building or get building ID."
          );
        }
        buildingId = addRes.response.entity_id;
        operationSuccessMessage = "Building added successfully!";
      }

      // --- HANDLE MEDIA UPLOAD (if a new file is selected) ---
      if (file && buildingId) {
        operationSuccessMessage += " Uploading image...";
        const baseUploadLocation = GetUploadMediaBaseLocation(
          cloudStorageBucket.buildings,
          user
        );
        const filePath = `${baseUploadLocation}${buildingId}/${Date.now()}_${file.name}`; // Add timestamp for uniqueness
        const storageRef = ref(storage, filePath);
        const uploadTask = uploadBytesResumable(storageRef, file);

        await new Promise((resolve, reject) => {
          uploadTask.on(
            "state_changed",
            (snapshot) => {
              const progress =
                (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
              setUploadProgress(progress);
              console.log(`Upload is ${progress}% done`);
            },
            (uploadError) => {
              console.error("Firebase upload failed:", uploadError);
              reject(
                new Error(
                  "Image upload failed. Building details saved/updated, but image upload failed."
                )
              );
            },
            async () => {
              try {
                const downloadURL = await getDownloadURL(
                  uploadTask.snapshot.ref
                );
                console.log("File available at", downloadURL);

                // Now, update your backend with this media information
                const mediaPayload = {
                  building_id: buildingId,
                  media: JSON.stringify([
                    {
                      // Assuming your backend expects an array of media objects
                      name: file.name,
                      location: filePath, // Store the Firebase storage path
                      url: downloadURL, // Store the public download URL
                      type: file.type,
                      size: file.size,
                      // description: "Building primary image", // Add description if needed
                    },
                  ]),
                };
                // This API call should ideally merge/replace media, not just add.
                // If it's just adding, you might accumulate old media.
                const mediaUpdateRes = await APICall(
                  "PUT",
                  paths.building.update,
                  mediaPayload,
                  undefined,
                  jwt
                ); // Or a specific media update endpoint
                if (mediaUpdateRes.status !== "success") {
                  throw new Error(
                    mediaUpdateRes.message ||
                      "Failed to associate media with building."
                  );
                }
                operationSuccessMessage = operationSuccessMessage.replace(
                  " Uploading image...",
                  " Image uploaded and associated successfully!"
                );
                resolve();
              } catch (mediaError) {
                reject(mediaError);
              }
            }
          );
        });
      }

      alert(operationSuccessMessage);
      navigate("/dashboard/buildings"); // Or to MyProperties or the specific building page
    } catch (err) {
      console.error("Operation failed:", err);
      setError(err.message || "An unexpected error occurred.");
    } finally {
      setIsLoading(false);
      setUploadProgress(0);
    }
  };

  return (
    // This component is assumed to be rendered within your main <Layout> via <Outlet />
    // So, the page padding (p-4 md:p-6 lg:p-8) comes from Layout's <main> tag.
    <div className="max-w-4xl mx-auto">
      {" "}
      {/* Constrain width for better readability on large screens */}
      <h1 className="text-2xl font-bold text-gray-800 mb-6">
        {isEditMode ? "Edit Building" : "Add New Building"}
      </h1>
      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Form is divided into sections for better organization */}
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h2 className="text-lg font-semibold text-gray-700 mb-4 border-b pb-2">
            Building Information
          </h2>

          {/* File Upload Section */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Building Image (Optional)
            </label>
            {/* <FileUpload onFileUpload={handleFileUpload} currentFile={file} /> */}
            <FileUpload onFileUpload={handleFileUpload} />{" "}
            {/* `currentFile={file}` prop might not be needed by FileUpload unless it uses it */}
            {/* Display progress during upload */}
            {isLoading && uploadProgress > 0 && uploadProgress < 100 && (
              <div className="w-full bg-gray-200 rounded-full h-2.5 mt-2">
                <div
                  className="bg-blue-600 h-2.5 rounded-full"
                  style={{ width: `${uploadProgress}%` }}
                ></div>
              </div>
            )}
            {/* Display existing image if editing and no new file selected */}
            {isEditMode && existingImageUrl && !file && (
              <div className="mt-4">
                <p className="text-xs text-gray-500 mb-1">Current Image:</p>
                {/* Assuming FileDownload can take a Firebase storage path or a full URL */}
                <FileDownload filePath={existingImageUrl} />
                {/* Or if you have a direct URL: */}
                {/* <img src={existingImageUrl} alt="Current building" className="max-h-40 rounded border"/> */}
              </div>
            )}
            {/* Display preview of newly selected file */}
            {file && filePreview && (
              <div className="mt-4">
                <p className="text-xs text-gray-500 mb-1">New Image Preview:</p>
                <img
                  // src={URL.createObjectURL(file)}
                  src={filePreview}
                  alt="New building preview"
                  className="max-h-40 rounded border"
                />
                {/* Button to remove/deselect the newly selected file */}
                <button
                  type="button"
                  onClick={() => {
                    if (filePreview) URL.revokeObjectURL(filePreview);
                    setFile(null);
                    setFilePreview(null);
                    setOldImageLocationToDelete(null); // Important: If user deselects, don't delete the original from Firebase

                    // Restore display of the original existing image
                    const originalExistingImageUrl =
                      buildingToEdit.media &&
                      buildingToEdit.media.length > 0 &&
                      buildingToEdit.media[0].location
                        ? buildingToEdit.media[0].location
                        : buildingToEdit.media &&
                            buildingToEdit.media.length > 0 &&
                            buildingToEdit.media[0].url
                          ? buildingToEdit.media[0].url
                          : "";
                    setExistingImageUrl(originalExistingImageUrl);
                  }}
                  className="mt-1 text-xs text-red-600 hover:text-red-800"
                >
                  Remove selection
                </button>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
            <FormInput
              label="Building Name"
              id="buildingName"
              name="buildingName"
              placeholder="e.g., Sunshine Apartments"
              value={formData.buildingName}
              onChange={handleInputChange}
              error={!formData.buildingName && "Building name is required"} // Example basic validation
            />
            <FormInput
              label="Locality / Address"
              id="locality"
              name="locality"
              placeholder="e.g., 123 Main St, Anytown"
              value={formData.locality}
              onChange={handleInputChange}
            />
          </div>

          <NumberStepper
            label="Number of Floors"
            value={formData.numberOfFloors}
            onIncrement={() =>
              setFormData((prev) => ({
                ...prev,
                numberOfFloors: prev.numberOfFloors + 1,
              }))
            }
            onDecrement={() =>
              setFormData((prev) => ({
                ...prev,
                numberOfFloors: Math.max(1, prev.numberOfFloors - 1),
              }))
            }
          />
          <NumberStepper
            label="Number of Units/Flats" // Changed label for consistency
            value={formData.numberOfFlats}
            onIncrement={() =>
              setFormData((prev) => ({
                ...prev,
                numberOfFlats: prev.numberOfFlats + 1,
              }))
            }
            onDecrement={() =>
              setFormData((prev) => ({
                ...prev,
                numberOfFlats: Math.max(1, prev.numberOfFlats - 1),
              }))
            }
          />
        </div>

        <div className="bg-white p-6 rounded-lg shadow-md">
          <h2 className="text-lg font-semibold text-gray-700 mb-4 border-b pb-2">
            Amenities
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            {amenitiesList.map((amenity) => (
              <div key={amenity} className="flex items-center">
                <input
                  type="checkbox"
                  id={amenity}
                  name="amenities" // For grouping
                  checked={formData.amenities.includes(amenity)}
                  onChange={handleCheckboxChange}
                  className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <label htmlFor={amenity} className="ml-2 text-sm text-gray-700">
                  {amenity}
                </label>
              </div>
            ))}
          </div>
        </div>

        {error && (
          <p className="text-sm text-red-600 bg-red-50 p-3 rounded-md">
            {error}
          </p>
        )}

        <div className="flex justify-end pt-4">
          <button
            type="button"
            onClick={() => navigate(-1)} // Go back
            className="bg-gray-200 text-gray-700 hover:bg-gray-300 px-6 py-2.5 rounded-md text-sm font-medium mr-3 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isLoading || !formData.buildingName} // Disable if loading or no building name
            className="bg-blue-600 text-white hover:bg-blue-700 px-6 py-2.5 rounded-md text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
          >
            {isLoading && (
              <svg
                className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
            )}
            {isLoading
              ? uploadProgress > 0
                ? `Uploading (${Math.round(uploadProgress)}%)...`
                : "Saving..."
              : isEditMode
                ? "Update Building"
                : "Save Building"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddNewBuilding;
